import {
  Drawer,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import { useMemo } from "react";
import BlockIcon from "@mui/icons-material/Block";
import DrawerHeader, { DRAWER_NO_SLIDE, DRAWER_PAPER_PROPS } from "./DrawerHeader";


const keyOf = (name) => name?.toString().toLowerCase().trim();

// Drawer de comparaison entre les sites en PRD et REC
const CompareDrawer = ({
  selectedFilteredIndex,
  selectedRecCounterpart,
  handleCloseDrawer,
  filteredData,
  originalData,
  role,
}) => {
  const theme = useTheme();

  // Un seul useMemo, placé avant tout early return pour respecter l'ordre des hooks :
  // les deux Map étaient reconstruites à chaque render, dont celui de l'ouverture, où
  // elles tombent dans la frame même où le Drawer démarre son animation.
  const compared = useMemo(() => {
    if (selectedRecCounterpart === null) return null;

    let prod = filteredData[selectedFilteredIndex];
    let rec = originalData[selectedRecCounterpart];
    if (!prod || !rec) return null;

    // swap values if compared from a REC site
    if (prod.content?.environnement === "REC") {
      const tmp = prod;
      prod = rec;
      rec = tmp;
    }

    const recPluginMap = new Map();
    (rec?.content?.plugins || []).forEach((p) => {
      if (p.name) recPluginMap.set(keyOf(p.name), p);
    });

    const recThemeMap = new Map();
    (rec?.content?.themes || []).forEach((t) => {
      if (t.name) recThemeMap.set(keyOf(t.name), t);
    });

    return {
      name: prod.content?.name,
      prodWpVersion: prod?.content?.wp_version || "N/A",
      recWpVersion: rec?.content?.wp_version || "N/A",
      prodPlugins: prod?.content?.plugins || [],
      prodThemes: prod?.content?.themes || [],
      recPluginMap,
      recThemeMap,
    };
  }, [filteredData, originalData, selectedFilteredIndex, selectedRecCounterpart]);

  if (!compared) return null;

  const { prodPlugins, prodThemes, recPluginMap, recThemeMap } = compared;

  return (
    <Drawer
      anchor="right"
      open={selectedRecCounterpart !== null}
      onClose={handleCloseDrawer}
      PaperProps={DRAWER_PAPER_PROPS.wide}
      SlideProps={DRAWER_NO_SLIDE}
      // Sans ça, le scroll lock de MUI pose overflow:hidden + padding-right sur
      // <body> à l'ouverture, ce qui relayoute tout le dashboard pendant l'animation.
      disableScrollLock
    >
      <div>
        {selectedRecCounterpart !== null &&(
          <div>
            <DrawerHeader
              title={`${compared.name} PRD vs REC`}
              onClose={handleCloseDrawer}
              role={role}
            />
            <div className="drawerTab">
              <h1 style={{ fontSize: "24px", fontWeight: "bold", paddingLeft:"15px" }}> Plugins</h1>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell align="left">Nom du composant</TableCell>
                      <TableCell align="center" style={{ fontWeight: "bold" }}>
                        Version en PROD
                      </TableCell>
                      <TableCell align="center" style={{ fontWeight: "bold" }}>
                        Version en RECETTE
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {prodPlugins.map((pluginPROD, i) => {
                        const pluginREC = recPluginMap.get(keyOf(pluginPROD.name));
                        return (
                        <TableRow
                            sx={{
                              backgroundColor: i % 2 === 0
                                ? theme.palette.background.alternate
                                : theme.palette.background.paper,
                            }}
                            key={pluginPROD.name}
                        >
                            <TableCell
                            style={{ width: "50%", fontWeight: "bold" }}
                            align="left"
                            >
                            {pluginPROD.name}
                            </TableCell>

                            <TableCell align="center" style={{ fontWeight: "bold" }}>
                            {pluginPROD.active === 1 ? (
                                pluginPROD.version
                            ) : (
                                <BlockIcon style={{ color: "red" }} />
                            )}
                            </TableCell>

                            <TableCell align="center" style={{ fontWeight: "bold" }}>
                            {pluginREC ? (
                                pluginREC.active === 1 ? pluginREC.version : <BlockIcon style={{ color: "red" }} />
                            ) : (
                                "N/A"
                            )}
                            </TableCell>
                        </TableRow>
                        );
                    })}
                    </TableBody>
                </Table>
              </TableContainer>
            </div>

            <div className="drawerTab">
              <h1 style={{ fontSize: "24px", fontWeight: "bold", paddingLeft:"15px" }}> Thèmes </h1>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell align="left">Nom du composant </TableCell>
                      <TableCell align="center" style={{ fontWeight: "bold" }}>
                        Version en PROD
                      </TableCell>
                      <TableCell align="center" style={{ fontWeight: "bold" }}>
                        Version en RECETTE
                      </TableCell>
                    </TableRow>
                  </TableHead>
                 <TableBody>
                {prodThemes.map((themePROD, i) => {
                    const themeREC = recThemeMap.get(keyOf(themePROD.name));
                    return (
                    <TableRow
                        sx={{
                          backgroundColor: i % 2 === 0
                            ? theme.palette.background.alternate
                            : theme.palette.background.paper,
                        }}
                        key={themePROD.name}
                    >
                        <TableCell
                        style={{ width: "50%", fontWeight: "bold" }}
                        align="left"
                        >
                        {themePROD.name}
                        </TableCell>

                        <TableCell align="center" style={{ fontWeight: "bold" }}>
                          {themePROD.active === 1 ? (
                                themePROD.version
                            ) : (
                                <BlockIcon style={{ color: "red" }} />
                            )}
                        </TableCell>
                        <TableCell align="center" style={{ fontWeight: "bold" }}>
                          {themeREC ? (
                            themeREC.active === 1 ? themeREC.version : <BlockIcon style={{ color: "red" }} />
                          ) : (
                            "N/A"
                          )}
                        </TableCell>
                    </TableRow>
                    );
                })}
                </TableBody>
                </Table>
              </TableContainer>
            </div>
            <div className="drawerTab">
              <h1 style={{ fontSize: "24px", fontWeight: "bold", paddingLeft:"15px" }}> Wordpress </h1>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell align="left">Nom du composant </TableCell>
                      <TableCell align="center" style={{ fontWeight: "bold" }}>
                        Version en PROD
                      </TableCell>
                      <TableCell align="center" style={{ fontWeight: "bold" }}>
                        Version en RECETTE
                      </TableCell>
                    </TableRow>
                  </TableHead>
                 <TableBody>
                    <TableRow
                        sx={{
                          backgroundColor: theme.palette.background.paper,
                        }}
                    >
                        <TableCell
                        style={{ width: "50%", fontWeight: "bold" }}
                        align="left"
                        >
                        WORDPRESS
                        </TableCell>

                        <TableCell align="center" style={{ fontWeight: "bold" }}>
                            {compared.prodWpVersion}
                        </TableCell>

                        <TableCell align="center" style={{ fontWeight: "bold" }}>
                            {compared.recWpVersion}
                        </TableCell>
                    </TableRow>
                </TableBody>
                </Table>
              </TableContainer>
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default CompareDrawer;
